import React, { useMemo } from 'react'
import numeral from 'numeraljs'
import moment from 'moment'
import classNames from 'classnames'

import styles from '../component-styles/metric-data.banner.module.scss'

import AnomalyDiff from './anomaly-diff'
import {
  dateFormatShort,
  dateFormatShortMonth,
  dateFormatShortWeek,
} from '../core/variables'
import { Preloader } from './loader'

interface MetricDataBannerProps {
  data: any
  successMetric: string
  // tooltip: string
  dateRange: string
  granularity: string
  loading?: boolean
}

export default function MetricDataBanner({
  data,
  successMetric,
  // tooltip,
  dateRange,
  granularity,
  loading = false,
}: MetricDataBannerProps): React.ReactElement | null {
  const {
    metricLastPeriod,
    metricLastPeriodComparison,
    // periodComparisonVsPeriodAverage,
    metricPeriodOnPeriodComparison,
    increasePositive,
    totalMetric,
    lastYearStartDate,
    lastPeriodStartDate,
    prevPeriodStartDate,
  } = data

  const useDateFormat = useMemo(() => {
    if (granularity === 'monthly') {
      return dateFormatShortMonth
    }
    if (granularity === 'weekly') {
      return dateFormatShortWeek
    }
    return dateFormatShort
  }, [granularity])

  const labelByPeriod = useMemo(() => {
    if (granularity === 'monthly') {
      return 'MoM'
    }
    if (granularity === 'weekly') {
      return 'WoW'
    }
    if (granularity === 'daily') {
      return 'DoD'
    }
    if (granularity === 'quarterly') {
      return 'QoQ'
    }
    return ''
  }, [granularity])

  let lastYearStartDateF = lastYearStartDate
    ? moment(lastYearStartDate, 'YYYY-MM-DD').format(useDateFormat)
    : ''
  let lastPeriodStartDateF = lastPeriodStartDate
    ? moment(lastPeriodStartDate, 'YYYY-MM-DD').format(useDateFormat)
    : ''
  let prevPeriodStartDateF = prevPeriodStartDate
    ? moment(prevPeriodStartDate, 'YYYY-MM-DD').format(useDateFormat)
    : ''

  const getQ = (dateS) => {
    if (dateS) {
      const d = moment(dateS, 'YYYY-MM-DD')
      const month = d.month() + 1
      const q = Math.floor((month + 3) / 3)
      return `Q${q} ${d.format('YYYY')}`
    }
    return ''
  }

  if (granularity === 'quarterly') {
    if (lastPeriodStartDate) {
      lastYearStartDateF = getQ(lastYearStartDate)
      lastPeriodStartDateF = getQ(lastPeriodStartDate)
      prevPeriodStartDateF = getQ(prevPeriodStartDate)
    }
  }

  if (!metricLastPeriod || loading) {
    return (
      <div className={styles.metricDataBannerWrapper}>
        <div className={styles.metricDataBanner}>
          <div
            className={classNames(styles.metricDataBannerBox, styles.isEmpty)}
          >
            <Preloader className={styles.loader} />
          </div>
          <div
            className={classNames(styles.metricDataBannerBox, styles.isEmpty)}
          >
            <Preloader className={styles.loader} />
          </div>
          <div
            className={classNames(styles.metricDataBannerBox, styles.isEmpty)}
          >
            <Preloader className={styles.loader} />
          </div>
        </div>
      </div>
    )
  }

  // if (metricLastPeriod.metricValue === 0) {
  //   return (
  //     <div className={styles.metricDataBannerWrapper}>
  //       <h2>
  //         {successMetric} <span className={styles.note}>{tooltip}</span>
  //       </h2>
  //       <div className={styles.metricDataBanner}>
  //         <div
  //           className={classNames(
  //             styles.metricDataBannerBox,
  //             styles.metricDataBannerBoxNoData,
  //           )}
  //         >
  //           <h2>No data for your selected filters</h2>
  //         </div>
  //       </div>
  //     </div>
  //   )
  // }

  return (
    <div className={styles.metricDataBannerWrapper}>
      <div className={styles.metricDataBanner}>
        <div className={styles.metricDataBannerBox}>
          <h2>Total {successMetric} </h2>
          <h3>{numeral(totalMetric.metricValue || 0).format('0,0.[0]a')} </h3>
          <h4>{dateRange}</h4>
        </div>
        <div className={styles.metricDataBannerBox}>
          <h2>{`${successMetric} ${labelByPeriod}`}</h2>
          <h3 className={styles.paddedTitle}>
            <AnomalyDiff
              className={styles.valueTotal}
              invertPolarity={!increasePositive}
              inline
              roundNumber
              noIcon
              noValueChange
              actual={metricLastPeriodComparison.metricLastCompleteValue || 0}
              expected={metricLastPeriodComparison.metricPrevValue || 0}
            />
            <AnomalyDiff
              className={styles.valueChange}
              invertPolarity={!increasePositive}
              inline
              roundNumber
              valueChangeOnly
              actual={metricLastPeriodComparison.metricLastCompleteValue || 0}
              expected={metricLastPeriodComparison.metricPrevValue || 0}
            />
          </h3>
          <h4>
            {lastPeriodStartDateF} vs {prevPeriodStartDateF}
          </h4>
        </div>
        <div className={styles.metricDataBannerBox}>
          <h2>{`${successMetric} YoY`}</h2>
          <h3 className={styles.paddedTitle}>
            <AnomalyDiff
              className={styles.valueTotal}
              invertPolarity={!increasePositive}
              inline
              roundNumber
              noIcon
              noValueChange
              actual={
                metricPeriodOnPeriodComparison.metricLastCompleteValue || 0
              }
              expected={metricPeriodOnPeriodComparison.metricPrevValue || 0}
            />
            <AnomalyDiff
              className={styles.valueChange}
              invertPolarity={!increasePositive}
              inline
              roundNumber
              valueChangeOnly
              actual={
                metricPeriodOnPeriodComparison.metricLastCompleteValue || 0
              }
              expected={metricPeriodOnPeriodComparison.metricPrevValue || 0}
            />
          </h3>
          <h4>
            {lastPeriodStartDateF} vs {lastYearStartDateF}
          </h4>
        </div>
        {/* <div className={styles.metricDataBannerBox}>
          <h2 className={styles.paddedTitle}>
            <AnomalyDiff
              invertPolarity={!increasePositive}
              arrowOnly
              largeIcon
              compact
              actual={
                periodComparisonVsPeriodAverage.metricLastCompleteValue || 0
              }
              expected={periodComparisonVsPeriodAverage.metricPrevValue || 0}
            />
            <AnomalyDiff
              invertPolarity={!increasePositive}
              inline
              roundNumber
              noIcon
              usePercentage={periodComparisonVsPeriodAverage.metricDisplay}
              actual={
                periodComparisonVsPeriodAverage.metricLastCompleteValue || 0
              }
              expected={periodComparisonVsPeriodAverage.metricPrevValue || 0}
            />
            {successMetric} vs avg
          </h2>
          <h4 className={styles.paddedTitle}>
            {lastPeriodStartDateF} vs prev {granularity} avg
          </h4>
        </div> */}
      </div>
    </div>
  )
}
